import { ComponentNames } from "../../constant";
import { dynamicRsnLookupToolAdapter } from "./rsn-lookup-tool-adapter";
import { getResolvedPageComponents } from "../../personalization/get-resolved-page-components";
import { dynamicChannelPackLineupAdapter } from "./channel-pack-lineup-adapter";
import { dynamicCustomerAcqCompareAdapter } from "./customer-acq-compare-adapter";
import { IApplicationFeatureConfig } from "../../types/application-feature-config";
import {
  IEpochOffers,
  IPackageConfig,
  IPersonalizationTriggers
} from "../../types/global";
import { dynamicPackageCompareToolAdapter } from "./package-compare-tool-adapeter";
import { dynamicChannelCompareToolPageAdapter } from "./channel-compare-tool-page-adapter";
import { transformAEMToSB } from "../../transformer/aem-to-sb-transformer";
import cloneDeep from "lodash/cloneDeep";
import { logError, logPageRequestDebug } from "../../helper/logger";
import { splitModalsFromCanvas } from "../../utils/split-modals-from-canvas";
import { pickChannels, pickChrome, buildLineupConfig, isChannelsServiceEnabled } from "../../helper/channels-service-helpers";

const ADAPTER_NAME = "dynamicPageAdapter";

interface IDynamicPageAdapter {
  pageComponents: any;
  personalizedComponents: any;
  personalizationTriggers: IPersonalizationTriggers;
  channelListingData: any;
  channelCompareToolPageData: any;
  queryParamsRequired: any;
  queryParams: any;
  applicationFeatureConfig?: IApplicationFeatureConfig;
  epochOffers: IEpochOffers;
  packagesConfig?: Array<IPackageConfig>;
  packagesConfigFeed?: any;
  genrePackOfferIds?: any;
  onDemandPackageConfig?: Array<IPackageConfig>;
  svodGenreData?: any;
  embeddedSvods: any;
  page?: string;
  rsnTierLegalTextObj?: any;
  personalizationMapping?: any;
  genreNationals?: any;
  pageSessionId?: string;
  svodDtvsPcp?: any;
  svodDtviPcp?: any;
  channelsServiceOtt?: any;
  channelsServiceSatellite?: any;
}

export function dynamicPageAdapter({
  pageComponents,
  personalizedComponents,
  personalizationTriggers,
  channelListingData,
  channelCompareToolPageData,
  queryParamsRequired,
  queryParams,
  applicationFeatureConfig,
  epochOffers,
  packagesConfig,
  packagesConfigFeed,
  genrePackOfferIds,
  onDemandPackageConfig,
  svodGenreData,
  embeddedSvods,
  page,
  rsnTierLegalTextObj,
  personalizationMapping,
  genreNationals,
  pageSessionId,
  svodDtvsPcp,
  svodDtviPcp,
  channelsServiceOtt,
  channelsServiceSatellite
}: IDynamicPageAdapter) {
  try {
    let cookieList;
    logPageRequestDebug(ADAPTER_NAME, `Dynamic Page Adaption for ${  page}`, {
      pageComponentsLength: pageComponents?.length || 0,
      personalizedComponentsLength: personalizedComponents?.length || 0,
      queryParamsRequired,
      queryParams,
      page,
      pageSessionId
    });
    let updatedPageComponents = pageComponents;

    // Request-time channels-service envelope, passed raw to every channel
    // sub-adapter. Each consumer picks its source via pickChannels (service
    // channels flag-ON + non-empty, else the legacy AEM channels as the flag-OFF /
    // degraded fallback).
    const channelsServiceData = channelsServiceOtt;

    // Request-time chrome (flag-aware), the mirror of the populate-time
    // `globalLineupConfig` in page-adapter. Built from the same package-config
    // feed via the shared `buildLineupConfig` so the object is byte-identical.
    // Gated on the flag: flag OFF -> undefined, so every `pickChrome` read below
    // falls through to the legacy AEM (`channelListingData`) value unchanged.
    const globalLineupConfig = isChannelsServiceEnabled()
      ? buildLineupConfig(packagesConfigFeed, packagesConfigFeed?.packageConfig)
      : undefined;

    if (personalizedComponents?.length > 0) {
      const personalizationData = getResolvedPageComponents(
        pageComponents,
        personalizedComponents,
        personalizationTriggers,
        page,
        personalizationMapping,
        pageSessionId
      );

      updatedPageComponents = personalizationData.components;
      cookieList = personalizationData.cookieList;
    }

    updatedPageComponents = updatedPageComponents.map((component, index) => {
      let newComponent = cloneDeep(component);
      switch (component.type) {
        case ComponentNames.CHANNEL_COMPARISON_TOOL_V2: {
          const channelCompareToolPageSBData = transformAEMToSB({
            aemPageData: channelCompareToolPageData
          });
          const channelCompareToolComponent =
            channelCompareToolPageSBData?.story?.content?.Body?.find(
              item => item.type === ComponentNames.CHANNEL_COMPARISON_TOOL
            ) || {};

          newComponent = dynamicChannelCompareToolPageAdapter({
            content: channelCompareToolComponent,
            componentId: "",
            cmsPageName: "",
            index,
            myFreeDTVConfig: applicationFeatureConfig?.myFreeDTVConfig?.[0],
            msbNationalRollOut: applicationFeatureConfig?.msbNationalRollOut,
            embeddedSvods,
            dtviEmbeddedSvodOffers: epochOffers?.dtviEmbeddedSvodOffers,
            dtvsEmbeddedSvodOffers: epochOffers.dtvsEmbeddedSvodOffers,
            isEmbeddedSvodsEnabled:
              applicationFeatureConfig?.embeddedSvodsConfig?.[0]
                ?.enableForChlup,
            queryParams,
            packagesConfig,
            rsnTierLegalTextObj,
            genreNationals,
            // Flag-ON channels-service envelopes (stream + satellite) so the modal
            // renders service channels at request time; pickChannels degrades to
            // aemChannels / content.channels flag-OFF or when the service is empty.
            channelsServiceData,
            channelsServiceSatelliteData: channelsServiceSatellite,
            aemChannels:
              channelCompareToolPageData?.[":items"]?.chnlCmpToolData?.elements
                ?.channels,
            aemChannelCategories:
              channelCompareToolPageData?.[":items"]?.chnlCmpToolData?.elements
                ?.channelCategories,
            globalLineupConfig
          });

          break;
        }

        case ComponentNames.PACKAGECOMPARETOOL:
          newComponent = dynamicPackageCompareToolAdapter({
            content: component.props,
            chnlListingPackages: pickChrome(
              globalLineupConfig?.packages,
              channelListingData?.[":items"]?.chnlListingPageData?.elements
                ?.packages
            ),
            chnlListingChannels:
              pickChannels(
                channelsServiceData?.channels,
                channelListingData?.[":items"]?.chnlListingPageData?.elements
                  ?.channels
              ),
            chnlListingMiniPacks: pickChrome(
              globalLineupConfig?.miniPacks,
              channelListingData?.[":items"]?.chnlListingPageData?.elements
                ?.miniPacks
            )?.options,
            genreOffers: epochOffers.genreSvodOffers,
            svodGenreData,
            packageConfig: packagesConfig,
            genrePackOfferIds,
            onDemandPackageConfig,
            genreLocalChannelMapping:
              applicationFeatureConfig?.genreLocalChannelMapping,
            getOffersData: epochOffers.dtviOffersPrices,
            myFreeDTVConfig: applicationFeatureConfig?.myFreeDTVConfig?.[0],
            embeddedSvods,
            dtviEmbeddedSvodOffers:
              epochOffers.dtviEmbeddedSvodOffers,
            genreNationals,
            creditOffers: epochOffers?.genreSvodOffers?.creditOffers,
            queryParams
          });
          break;

        case ComponentNames.CHANNELPACKLINEUP:
          newComponent.props = dynamicChannelPackLineupAdapter({
            componentProps: component.props,
            channelListingData,
            channelsServiceData,
            globalLineupConfig,
            genreOffers: epochOffers.genreSvodOffers,
            svodGenreData,
            packageConfig: packagesConfig,
            onDemandPackageConfig,
            myFreeDTVConfig: applicationFeatureConfig?.myFreeDTVConfig?.[0],
            queryParams
          });

          break;

        case ComponentNames.CUSTOMERACQCOMPARETOOL:
          newComponent.props = dynamicCustomerAcqCompareAdapter({
            componentProps: component.props,
            channelListingData,
            channelsServiceData,
            rsnTierLegalText: rsnTierLegalTextObj,
            genreLocalChannelMapping: applicationFeatureConfig?.genreLocalChannelMapping,
            signatureExcludedLocalChannels: applicationFeatureConfig?.signatureExcludedLocalChannels,
            embeddedSvods,
            embeddedIncludedSvods: epochOffers.dtviEmbeddedSvodOffers,
            getOffersGenreData: epochOffers?.genreSvodOffers,
            offerPrices: epochOffers.dtviOffersPrices,
            byodPlanCardOffers: epochOffers?.byodPlanCardOffers,
            genreNationals,
            packagesConfig,
            genrePackOfferIds,
            creditOffers: epochOffers?.genreSvodOffers?.creditOffers,
            queryParams
          });
          break;
        case ComponentNames.CHLUP_INTERACTIVE_TOOL:
          newComponent.props = {
            ...component.props,
            svodDtvsPcp,
            svodDtviPcp,
            dtvsSvodOffers: epochOffers?.dtvsSvodOffers,
            dtviSvodOffers: epochOffers?.dtviSvodOffers
          };
          break;
        case ComponentNames.RSNLOOKUPTOOL:
          newComponent.props = dynamicRsnLookupToolAdapter({
            channelListingData,
            channelsServiceData,
            queryParams,
            componentProps: component.props
          });

          break;
        case ComponentNames.CHANNEL_LISTING_PAGE:
          if (queryParams?.view) {
            const viewParam = Array.isArray(queryParams.view) ? queryParams.view[0] : queryParams.view;
            newComponent.props = {
              ...component.props,
              config: {
                ...component.props?.config,
                viewQueryParam: viewParam.toLowerCase()
              }
            };
          }
          break;
      }
      return newComponent;
    });

    // WAT-10279: Separate modal components from canvas components.
    // The orchestrator is the sole renderer for modals — the canvas
    // should never see them. modalComponents preserves the original
    // { type, props } shape so resolveModalFeeds can resolve page-level ?? global.
    const { canvasComponents, modalComponents } = splitModalsFromCanvas(updatedPageComponents);

    return {
      components: canvasComponents,
      modalComponents,
      cookieList
    };
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      pageComponents,
      pageComponentsLength: pageComponents?.length || 0,
      personalizedComponentsLength: personalizedComponents?.length || 0,
      queryParamsRequired,
      queryParams,
      page
    });
    return {
      components: pageComponents,
      modalComponents: []
    };
  }
}

export default dynamicPageAdapter;
